import { Drawer } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { SearchIconStroke } from '../../../../../../assets/icons/common/common-icons';
import { useStore } from '../../../../../app/data/root.store';
import { getDocTypeIcon } from '../../../../assets/docsTypes/docTypes';
import { SmartTagIcon } from './SmartTagIcon';
import {
    AddCustomTagButton,
    AllTagsWrapper,
    CheckmarkIcon,
    CreateTagInput,
    CreateTagInputWrapper,
    CreateTagLabel,
    CreateTagSection,
    DocumentIcon,
    DocumentInfo,
    DocumentName,
    DocumentSection,
    DrawerBody,
    DrawerCloseButton,
    DrawerHeader,
    DrawerTitle,
    DrawerWrapper,
    PlusIcon,
    SaveButton,
    SaveButtonContainer,
    SearchIcon,
    SearchInput,
    SearchInputWrapper,
    SearchSection,
    SelectedTag,
    SelectedTagContent,
    SelectedTagIcon,
    SelectedTagText,
    TagOption,
    TagOptionContent,
    TagOptionIcon,
    TagOptionText,
    TagSection
} from './TagEditorDrawer.styles';
import { PREDEFINED_TAG_NAMES } from '../../../../data/vault-store';

export const TagEditorDrawer = observer(({
    isOpen,
    onClose,
    selectedTags = [],
    onSave,
}) => {
    const { vaultStore } = useStore();
    const [newTagInput, setNewTagInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [localSelectedTags, setLocalSelectedTags] = useState([]);
    const [initiallySelectedTagIds, setInitiallySelectedTagIds] = useState(new Set());
    const fileExtension = vaultStore.selectedFile?.originalName?.split('.').pop()?.toLowerCase();

    useEffect(() => {
        if (isOpen) {
            setLocalSelectedTags(selectedTags);
            setInitiallySelectedTagIds(new Set(selectedTags.map(tag => tag.id)));
            if (vaultStore.userTags.length === 0) {
                vaultStore.fetchUserTags();
            }
        }
    }, [isOpen, vaultStore, selectedTags]);

    const filteredTags = useMemo(() => {
        if (!searchTerm) {
            // No search term - return all available tags (includes predefined)
            return vaultStore.allAvailableTags;
        } else {
            // Filter available tags by search term, but always include predefined tags
            return vaultStore.allAvailableTags.filter(tag =>
                tag.isPredefined || // Always include predefined tags
                tag.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tag.id.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
    }, [vaultStore.allAvailableTags, searchTerm]);

    // Get all tags with wasSelected property and sort them
    const allTagsWithStatus = useMemo(() => {
        const tagsWithStatus = filteredTags.map(tag => ({
            ...tag,
            wasSelected: initiallySelectedTagIds.has(tag.id),
            isSelected: localSelectedTags.some(selectedTag => selectedTag.id === tag.id)
        }));

        // Add custom tags that were created (including those without isCustom flag)
        localSelectedTags.forEach(selectedTag => {
            const existsInAvailableTags = tagsWithStatus.some(tag => tag.id === selectedTag.id);
            const isCustomTag = selectedTag.isCustom || !vaultStore.allAvailableTags.some(tag => tag.id === selectedTag.id);
            
            if (isCustomTag && !existsInAvailableTags) {
                tagsWithStatus.push({
                    ...selectedTag,
                    isCustom: true,
                    wasSelected: initiallySelectedTagIds.has(selectedTag.id),
                    isSelected: true
                });
            }
        });

        // Simplified, more efficient sorting
        return tagsWithStatus.sort((a, b) => {
            const aIsNewCustom = a.isCustom && !a.wasSelected;
            const bIsNewCustom = b.isCustom && !b.wasSelected;
            
            if (aIsNewCustom !== bIsNewCustom) return aIsNewCustom ? -1 : 1;
            if (a.wasSelected !== b.wasSelected) return a.wasSelected ? -1 : 1;

            const aLabel = (a.label || a.id || '').toLowerCase();
            const bLabel = (b.label || b.id || '').toLowerCase();

            if ((aLabel === 'other') !== (bLabel === 'other')) {
                return aLabel === 'other' ? 1 : -1;
            }

            return aLabel.localeCompare(bLabel);
        });
    }, [filteredTags, localSelectedTags, initiallySelectedTagIds, vaultStore.allAvailableTags]);

    const renderTagOption = (tag) => {
        const isSelected = tag.isSelected;

        if (isSelected) {
            return (
                <SelectedTag key={tag.id} onClick={() => handleTagToggle(tag)}>
                    <SelectedTagContent>
                        <SelectedTagIcon>
                            <SmartTagIcon tagName={tag.id} size={24} />
                        </SelectedTagIcon>
                        <SelectedTagText>{tag.label}</SelectedTagText>
                        <CheckmarkIcon>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.5103 8.604L10.5059 15.6125L6.99983 12.1075" stroke="white" strokeWidth="2" />
                            </svg>
                        </CheckmarkIcon>
                    </SelectedTagContent>
                </SelectedTag>
            );
        } else {
            return (
                <TagOption key={tag.id} onClick={() => handleTagToggle(tag)}>
                    <TagOptionContent>
                        <TagOptionIcon>
                            <SmartTagIcon tagName={tag.id} size={24} />
                        </TagOptionIcon>
                        <TagOptionText>{PREDEFINED_TAG_NAMES[tag.label] || tag.label}</TagOptionText>
                    </TagOptionContent>
                </TagOption>
            );
        }
    };

    const handleTagToggle = useCallback((tag) => {
        setLocalSelectedTags(prev => {
            const isSelected = prev.some(t => t.id === tag.id);
            if (isSelected) {
                return prev.filter(t => t.id !== tag.id);
            } else {
                return [...prev, tag];
            }
        });
    }, []);

    const handleCreateTag = () => {
        if (newTagInput.trim()) {
            const customTag = {
                id: newTagInput.toLowerCase(),
                label: newTagInput,
                isCustom: true,
                wasSelected: false
            };

            const isAlreadySelected = localSelectedTags.some(tag =>
                tag.id.toLowerCase() === customTag.id.toLowerCase()
            );

            if (!isAlreadySelected) {
                setLocalSelectedTags([customTag, ...localSelectedTags]);
            }

            setNewTagInput('');
        }
    };

    const hasValidInput = newTagInput.trim().length > 0;

    const handleSave = () => {
        onSave(localSelectedTags);
        setTimeout(() => {
            onClose();
        }, 0);
    };

    return (
        <Drawer
            closable={false}
            visible={isOpen}
            width={'100%'}
            height={'auto'}
            className="tag-editor-drawer-mobile"
            placement="bottom"
            destroyOnClose={true}
            onClose={onClose}
        >
            <DrawerWrapper>
                <DrawerHeader>
                    <DrawerTitle>Tags</DrawerTitle>
                    <DrawerCloseButton onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#9097A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </DrawerCloseButton>
                </DrawerHeader>

                <DrawerBody>
                    <DocumentSection>
                        <DocumentInfo>
                            <DocumentIcon>
                                {getDocTypeIcon(fileExtension)}
                            </DocumentIcon>
                            <DocumentName>{vaultStore.selectedFile?.displayName}</DocumentName>
                        </DocumentInfo>
                        {localSelectedTags.length > 0 && (
                            <div style={{
                                background: '#1A1B1D',
                                color: '#FFFFFF',
                                borderRadius: '12px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}>
                                {localSelectedTags.length} tag{localSelectedTags.length !== 1 ? 's' : ''}
                            </div>
                        )}
                    </DocumentSection>

                    <SearchSection>
                        <SearchInputWrapper>
                            <SearchInput
                                type="text"
                                placeholder="Search tags"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <SearchIcon><SearchIconStroke /></SearchIcon>
                        </SearchInputWrapper>
                    </SearchSection>

                    <CreateTagSection>
                        <CreateTagLabel>Create new tag for this file</CreateTagLabel>
                        <CreateTagInputWrapper>
                            <PlusIcon>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 2V14M2 8H14" stroke="#1A1B1D" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            </PlusIcon>
                            <CreateTagInput
                                type="text"
                                placeholder="Create new type tag"
                                value={newTagInput}
                                autoFocus
                                onChange={(e) => setNewTagInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && hasValidInput && handleCreateTag()}
                            />
                            {hasValidInput && (
                                <AddCustomTagButton onClick={handleCreateTag}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M3 8L7 12L13 6" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </AddCustomTagButton>
                            )}
                        </CreateTagInputWrapper>
                    </CreateTagSection>

                    <TagSection>
                        <AllTagsWrapper>
                            {allTagsWithStatus.map(renderTagOption)}
                        </AllTagsWrapper>
                    </TagSection>
                </DrawerBody>

                <SaveButtonContainer>
                    <SaveButton onClick={handleSave}>
                        Save {localSelectedTags.length} tag{localSelectedTags.length !== 1 ? 's' : ''}
                    </SaveButton>
                </SaveButtonContainer>
            </DrawerWrapper>
        </Drawer>
    );
}); 